import React from 'react';
import styled from 'styled-components';
import { theme } from '../../home/styled/theme';
import { Card as BaseCard, Flex, ButtonLink, FontBold, TextMuted } from '../../home/styled/BaseComponents';

// Old design (default for /auction route)
const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 48px;
    height: 48px;
    color: #9ca3af;
  }
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Symbol = styled.div`
  font-size: 0.875rem;
  color: #5ce9ff;
  font-weight: 500;
`;

const CountdownText = styled.span`
  font-size: 0.875rem;
  color: #9ca3af;
`;

const EndedBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ComingSoonBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  background: rgba(92, 233, 255, 0.2);
  color: #5ce9ff;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ViewButton = styled.button`
  margin-top: auto;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// New design (for homepage)
const AuctionCardItem = styled(BaseCard)`
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ItemContent = styled(Flex)`
  align-items: flex-start;
  flex-wrap: nowrap;
  flex-direction: column;
  gap: 0.75rem;
`;

const ItemIcon = styled.div`
  height: 46px;
  width: 56px;
  border-radius: 0.75rem;
  background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent2});
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemText = styled.div`
  min-width: 0;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ItemTitle = styled(FontBold)`
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${theme.colors.text};
`;

const ItemSubtitle = styled(TextMuted)`
  font-size: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HomepageCountdownText = styled(TextMuted)`
  font-size: 12px;
`;

const ItemActions = styled(Flex)`
  margin-top: auto;
  gap: 0.5rem;
  align-items: center;
`;

const SmallButton = styled(ButtonLink)`
  font-size: 14px;
`;

const AuctionCard = ({ 
  auction, 
  onClick, 
  countdown,
  isComingSoon = false,
  showImage = true,
  homepageStyle = false // New prop to switch between styles
}) => {
  const isEnded = countdown === 'ended' || countdown === 'Ended';

  // Extract product info from auction data
  const productInfo = auction?.product_info?.[0] || auction?.product_info || null;
  const productTitle = productInfo?.title || auction?.title || 'Untitled Auction';
  const productDescription = productInfo?.description || auction?.description || 'No description available.';
  const productImages = productInfo?.pictures || auction?.pictures || [];
  const productImage = productImages.length > 0 ? productImages[0] : null;

  // Homepage style (Marketplace design)
  if (homepageStyle) {
    return (
      <AuctionCardItem onClick={onClick}>
        <ItemContent gap="0.75rem">
          <ItemIcon>
            {productImage ? (
              <ItemImage src={productImage} alt={productTitle || 'Auction image'} loading="lazy" />
            ) : null}
          </ItemIcon>
          <ItemText>
            <ItemTitle>{productTitle}</ItemTitle>
            <ItemSubtitle>{productDescription}</ItemSubtitle>
            {countdown && (
              <HomepageCountdownText>
                {isComingSoon ? (
                  `Starts in ${countdown}`
                ) : isEnded ? (
                  'Ended'
                ) : (
                  `Ends in ${countdown}`
                )}
              </HomepageCountdownText>
            )}
          </ItemText>
        </ItemContent>
        <ItemActions>
          <SmallButton to="/sign-in">Buy</SmallButton>
          <SmallButton to="/sign-in">View</SmallButton>
        </ItemActions>
      </AuctionCardItem>
    );
  }

  // Old style (default for /auction route)
  return (
    <Card onClick={onClick}>
      {showImage && (
        <ImageContainer>
          {productImage ? (
            <img 
              src={productImage} 
              alt={productTitle}
              onError={(e) => {
                e.target.style.display = 'none';
                const svg = e.target.nextElementSibling;
                if (svg) svg.style.display = 'block';
              }}
            />
          ) : null}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            style={{ display: productImage ? 'none' : 'block' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </ImageContainer>
      )}
      
      <Title>{productTitle}</Title>
      
      <InfoRow>
        {auction.symbol && <Symbol>{auction.symbol}</Symbol>}
        {isComingSoon ? (
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
        ) : isEnded ? (
          <EndedBadge>Ended</EndedBadge>
        ) : countdown ? (
          <CountdownText>Ends in {countdown}</CountdownText>
        ) : null}
      </InfoRow>

      <ViewButton onClick={(e) => {
        e.stopPropagation();
        onClick && onClick();
      }}>
        View Auction
      </ViewButton>
    </Card>
  );
};

export default AuctionCard;
